import React, { useState, useEffect } from 'react';
import { groceryAPI } from '../services/api';
import { GroceryItem } from '../types';
import { CATEGORIES } from '../utils/constants';
import { Modal } from './common/Modal';
import { Input } from './common/Input';
import { Select } from './common/Select';
import { Button } from './common/Button';
import { EmojiPicker } from './common/EmojiPicker';

interface GroceryItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  listId: string | null; // target list for new items
  itemToEdit?: GroceryItem | null;
  onDelete?: () => void;
}

const GroceryItemModal: React.FC<GroceryItemModalProps> = ({
  isOpen,
  onClose,
  onSave,
  listId,
  itemToEdit = null,
  onDelete
}) => {
  const [formData, setFormData] = useState({
    name: '',
    emoji: CATEGORIES[0]?.emoji || '🛒',
    type: (CATEGORIES[0]?.name || 'Other') as GroceryItem['type'],
    quantity: 1
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (itemToEdit) {
        setFormData({
          name: itemToEdit.name,
          emoji: itemToEdit.emoji,
          type: itemToEdit.type,
          quantity: itemToEdit.quantity
        });
      } else {
        setFormData({
          name: '',
          emoji: CATEGORIES[0]?.emoji || '🛒',
          type: (CATEGORIES[0]?.name || 'Other') as GroceryItem['type'],
          quantity: 1
        });
      }
      setError(null);
    }
  }, [isOpen, itemToEdit]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'type') {
      const category = CATEGORIES.find(cat => cat.name === value);
      setFormData(prev => ({
        ...prev,
        type: value as GroceryItem['type'],
        emoji: category?.emoji || '🛒'
      }));
    } else if (name === 'quantity') {
      setFormData(prev => ({
        ...prev,
        quantity: Math.max(1, parseInt(value) || 1)
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (itemToEdit && itemToEdit._id) {
        await groceryAPI.update(itemToEdit._id, formData);
      } else {
        if (!listId) { setError('Aucune liste sélectionnée'); setLoading(false); return; }
        await groceryAPI.create(listId, { ...formData, important: false, bought: false });
      }
      onSave();
      onClose();
    } catch (err: any) {
      console.error('Error saving item:', err);
      setError(err.response?.data?.message || 'Failed to save item');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    if (!itemToEdit?._id || !onDelete) return;
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) return;
    onDelete();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={itemToEdit ? "Modifier l'article" : "Ajouter un nouvel article"}
    >
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-danger rounded-lg text-danger text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">

        {/* Live preview */}
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
          <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-white shadow-soft text-2xl flex-shrink-0">
            {formData.emoji}
          </span>
          <div className="min-w-0">
            <div className="font-semibold text-gray-900 truncate">{formData.name || 'Nouvel article'}</div>
            <div className="text-xs text-gray-400">{formData.type} · x{formData.quantity}</div>
          </div>
        </div>

        <Input
          label="Nom de l'article"
          type="text"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          required
          disabled={loading}
        />

        <Select
          label="Catégorie"
          name="type"
          value={formData.type}
          onChange={handleInputChange}
          required
          disabled={loading}
          options={CATEGORIES.map(cat => ({
            value: cat.name,
            label: `${cat.emoji} ${cat.name}`
          }))}
        />

        <EmojiPicker
          value={formData.emoji}
          onChange={(emoji) => setFormData((prev) => ({ ...prev, emoji }))}
          disabled={loading}
        />

        <Input
          label="Quantité"
          type="number"
          name="quantity"
          value={formData.quantity.toString()}
          onChange={handleInputChange}
          required
          min={1}
          disabled={loading}
        />

        <div className="flex items-center justify-between gap-3 pt-2">
          {itemToEdit && onDelete && (
            <Button type="button" variant="danger" size="md" onClick={handleDelete} disabled={loading}>
              Supprimer
            </Button>
          )}
          <div className="flex gap-2 ml-auto">
            <Button type="button" variant="secondary" size="md" onClick={onClose} disabled={loading}>
              Annuler
            </Button>
            <Button type="submit" variant="primary" size="md" disabled={loading} loading={loading}>
              {itemToEdit ? 'Sauvegarder' : 'Ajouter'}
            </Button>
          </div>
        </div>

      </form>
    </Modal>
  );
};

export default GroceryItemModal;
